<template>
  <div class="auth-section">
    <div class="form-wrapper">
      <div class="form-section">
        <!-- Dynamic title and subtitle -->
        <div class="input-title">
          <h2>{{ $t(type === 'login' ? 'auth.login.welcome' : 'auth.signup.welcome') }}</h2>
          <p>{{ $t(type === 'login' ? 'auth.login.subtitle' : 'auth.signup.subtitle') }}</p>
        </div>

        <!-- Persistent toggle with animation -->
        <div class="auth-switch">
          <div class="auth-toggle">
            <div class="toggle-slider" :class="{ 'slide-right': type === 'signup' }"></div>
            <button
              type="button"
              :class="['toggle-btn', { active: type === 'login' }]"
              @click="display('login')"
            >
              {{ $t('auth.login.title') }}
            </button>
            <button
              type="button"
              :class="['toggle-btn', { active: type === 'signup' }]"
              @click="display('signup')"
            >
            {{ $t('auth.signup.title') }}
            </button>
          </div>
        </div>

        <component-form
          :authType="this.type"
          v-on:submit-clicked="submit"
          v-on:forgot-clicked="forgot"
        ></component-form>
        <component-social
          v-on:facebook-connected="createFacebook"
          v-on:linkedin-connected="createLinkedin"
        ></component-social>

        <div class="login-link">
          <p>
            <a class="underline legal" href="/#/legal" target="_blank">{{
              $t('dash.header.nav.legal')
            }}</a>
          </p>
        </div>
      </div>
      <img
        v-if="confirmRegister"
        src="https://ct.capterra.com/capterra_tracker.gif?vid=2157106&vkey=cacb528208ed853298cc3fe4045c7759"
      />
    </div>
    <component-carousel :authType="this.type"></component-carousel>

    <!-- Loading Overlay -->
    <LoadingOverlay :show="isLoading" />

    <!-- Custom Modal -->
    <CustomModal
      :show="dialog.show"
      :title="dialog.title"
      :message="dialog.message"
      @close="hideDialog"
    />
  </div>
</template>

<script>
import componentCarousel from './Carousel.vue'
import componentForm from './Form.vue'
import componentSocial from './Social.vue'

// Shared components
import CustomModal from './shared/CustomModal.vue'
import LoadingOverlay from './shared/LoadingOverlay.vue'

// Services imports
import authService from './services/auth/authService.js'
import passwordService from './services/auth/passwordService.js'
import socialAuthService from './services/auth/socialAuthService.js'

import Common from '../../components/mixins/Common'
import ErrorHandler from './mixins/ErrorHandler.vue'

export default {
  name: 'component-layout',

  mixins: [Common, ErrorHandler],

  components: {
    componentForm,
    componentCarousel,
    componentSocial,
    LoadingOverlay,
    CustomModal
  },

  data() {
    return {
      c_key: '',
      confirmRegister: false,
      isLoading: false
    }
  },

  async mounted() {
    const { key } = this.$route.query
    if (key) this.c_key = key

    //Check confirmation mail
    let currentRoute = this.$router.currentRoute

    if (
      currentRoute.name === 'confirm' &&
      currentRoute.query.token !== undefined
    ) {
      // Validate token format before sending to API
      const token = currentRoute.query.token
      if (!token || typeof token !== 'string' || token.trim() === '') {
        this.handleAuthError({ error: 'Invalid confirmation token' })
        return
      }

      this.confirmRegister = true
      this.isLoading = true

      try {
        const response = await authService.verify(token)

        // httpService returns { success, data, response }
        if (!response || !response.data) {
          this.isLoading = false
          this.handleAuthError({ error: 'Invalid response from server' })
          return
        }

        const { success, token: authToken, user } = response.data

        this.isLoading = false

        if (success !== true || 'error' in response.data) {
          this.handleAuthError({ body: response.data })
          return
        }

        // Store auth data and navigate to dashboard
        this.goToDash({ token: authToken, user })
      } catch (error) {
        this.isLoading = false
        this.handleAuthError(error)
      }
    }
  },

  props: {
    type: {
      type: String,
      required: true
    }
  },

  methods: {
    /**
     * Change layout content - signup/login components
     */
    display(typeForm) {
      let type = typeForm
      this.$emit('toggle-type', type)
    },

    /**
     * Navigate to dashboard using authService
     * @param {Object} params - Params to send to dash
     */
    goToDash(params) {
      authService.handleAuthSuccess({
        token: params.token,
        user: params.user
      })

      if (!('dry' in params) || params.dry === false) {
        // const isNotProduction = process.env.NODE_ENV !== 'production'
        const user = params.user
        const isAdmin = !!(user && user.admin)
        const hasActivePlan = user && user._premium && user._premium.active

        // if (isNotProduction && !hasActivePlan) {
        if (isAdmin &&!hasActivePlan) {
          // No active plan → onboarding needs + payment
          window.location = '/#/onboarding/needs'
        } else {
          // Has plan (or production) → dash (router guard checks payment method + pages),
          // unless we were sent here mid-flow (e.g. OAuth "remote connector") and saved where
          // to go back to - see router/index.js's 'dash' beforeEnter guard.
          const postLoginRedirect = localStorage.getItem('post_login_redirect')
          localStorage.removeItem('post_login_redirect')
          window.location = postLoginRedirect ? `/#${postLoginRedirect}` : '/#/dash'
        }
      }
    },

    /**
     * Submit the auth form
     * @param {Object} form - The form to format
     */
    async submit(form) {
      let { email, password, firstname, lastname, phone, trial, promo } = form

      // Check required fields
      if (!email || !password) {
        this.isLoading = false
        return
      }

      const normalizedEmail = email.trim().toLowerCase()

      // Show user something happens
      this.isLoading = true

      // Call appropriate service method
      try {
        if (this.$ga) {
          this.$ga.event('auth', this.type)
        }
      } catch (e) {
        console.warn('Analytics not available:', e)
      }

      let response
      try {
        if (this.type === 'signup') {
          response = await authService.signup(
            {
              email: normalizedEmail,
              password,
              firstname,
              lastname,
              phone,
              trial,
              promo,
              locale: this.$i18n.locale
            },
            location.search
          )
        } else {
          response = await authService.login({
            email: normalizedEmail,
            password
          })
        }
      } catch (error) {
        return this.handleAuthError(error)
      }

      this.isLoading = false

      if (!response.success || response.error) {
        return this.handleAuthError(response)
      }

      // Extract data from service response format
      const { token, user } = response.data

      if (this.type !== 'signup') {
        this.goToDash({ token, user })
      } else {
        // Save promo code for use during onboarding payment step
        if (promo) {
          localStorage.setItem('promo', promo)
        }
        // Show signup success message with custom modal
        this.showDialog(
          this.$t('auth.success.signup.title'),
          this.$t('auth.success.signup.message')
        )
      }
    },

    /**
     * Forgot password using passwordService
     * @param {string} email - User email
     */
    async forgot(email) {
      try {
        if (this.$ga) {
          this.$ga.event('auth', 'forgot-password')
        }
      } catch (e) {
        console.warn('Analytics not available:', e)
      }

      try {
        const response = await passwordService.forgotPassword({ email })

        if (!response.success) {
          return this.handleAuthError(response)
        }

        // Show forgot password success with custom modal
        this.showDialog(
          this.$t('auth.success.forgot.title'),
          this.$t('auth.success.forgot.message')
        )
      } catch (error) {
        this.handleAuthError(error)
      }
    },

    /**
     * Create facebook user using socialAuthService
     * @param {Object} form - The form to format
     */
    async createFacebook(form, pages) {
      let body = {
        id: form.id,
        token: form.token
      }

      try {
        const response = await socialAuthService.verifyFacebook(body)
        const { success, token, user } = response.data

        if (!success || success !== true) {
          this.handleAuthError(response)
          return
        }

        localStorage.setItem('facebook', JSON.stringify(body))
        let params = { token: token, user: user }
        this.goToDash(params)
      } catch (error) {
        this.handleAuthError(error)
      }
    },

    /**
     * Create Linkedin user
     * @param {Object} form - The form to format
     */
    createLinkedin(data) {
      this.goToDash(data)
    }
  }
}
</script>
